/**
 * ESS Climate Intelligence — Interactive Pacific ENSO Map
 * Leaflet + NOAA NCEI THREDDS WMS SST / SST Anomaly tiles
 * with Niño 3.4 box overlay and time slider
 */

import L from 'leaflet';
import { infoTrigger, infoPanel, setInfoMeaning } from './InfoNote.js';
import { fetchCurrentStatus } from '../data/noaa.js';
import { DEFAULT_DASHBOARD_REGION } from '../data/regions.js';

// NASA GIBS WMS endpoint for SST (NOAA THREDDS is currently down)
const OISST_WMS = 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi';

// Base map tiles — ESRI Dark Gray Canvas (free, no API key required)
const BASE_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}';

// Time range for slider: Jan 2026 → Aug 2026
const SLIDER_MONTHS = [
  '2026-01-15', '2026-02-15', '2026-03-15', '2026-04-15',
  '2026-05-15', '2026-06-15', '2026-07-15', '2026-08-15',
];
const SLIDER_LABELS = [
  'Jan 2026', 'Feb 2026', 'Mar 2026', 'Apr 2026',
  'May 2026', 'Jun 2026', 'Jul 2026', 'Aug 2026',
];

const PACIFIC_REGION_VIEWS = {
  pakistan: {
    label: 'Pakistan',
    center: [24, 67],
    zoom: 5,
    bounds: [[22, 58], [38, 78]],
    marker: [[22, 58], [38, 78]],
  },
  asia: {
    label: 'Asia',
    center: [7, 105],
    zoom: 3,
    bounds: [[-12, 55], [48, 150]],
    marker: [[-8, 55], [48, 150]],
  },
  iofs: {
    label: 'IOFS Members',
    center: [10, 55],
    zoom: 2,
    bounds: [[-35, -20], [58, 112]],
    marker: [[-35, -20], [58, 112]],
  },
  australia: {
    label: 'Australia',
    center: [-23, 135],
    zoom: 4,
    bounds: [[-45, 108], [-8, 158]],
    marker: [[-45, 108], [-8, 158]],
  },
  'new-zealand': {
    label: 'New Zealand',
    center: [-41, 173],
    zoom: 5,
    bounds: [[-49, 165], [-33, 180]],
    marker: [[-49, 165], [-33, 180]],
  },
};

export function createPacificMap() {
  const section = document.createElement('section');
  section.id = 'pacific-map';
  section.className = 'section';
  section.innerHTML = `
    <div class="container">
      <div class="section-header anim-on-scroll">
        <div class="section-header__eyebrow">Section 01</div>
        <h2 class="section-header__title">The Pacific Ocean Signal</h2>
        <p class="section-header__subtitle">
          El Niño originates in the equatorial Pacific. The <strong>Niño 3.4 region</strong>
          (5°N–5°S, 170°W–120°W) is the primary monitoring zone. Watch how sea surface
          temperatures have evolved through 2026.
        </p>
      </div>
    </div>

    <div class="container container--wide">
      <div class="pacific-map-wrap anim-on-scroll">
        <!-- Controls Bar -->
        <div class="pacific-controls glass-card">
          <div class="pacific-controls__left">
            <div class="sst-mode-toggle" id="sstModeToggle">
              <button class="sst-mode-toggle__btn active" data-mode="anom" id="btnAnom" type="button">SST Anomaly</button>
              <button class="sst-mode-toggle__btn" data-mode="sst" id="btnSST" type="button">Sea Surface Temp</button>
            </div>
          </div>
          <div class="pacific-controls__right">
            <div class="live-badge">
              <span class="live-dot"></span>
              NOAA OISST
            </div>
          </div>
        </div>

        <p class="pacific-mode-explain" id="pacificModeExplain">
          This map shows where the Pacific Ocean is warmer or cooler than normally expected for this time of year.
          <span class="pacific-mode-explain__sub">Actual SST tells us how warm the ocean is. SST anomaly tells us how unusual that temperature is.</span>
        </p>

        <!-- Map -->
        <div class="map-wrap" style="height: 480px;" id="pacificMapContainer">
          <div class="pacific-map-status" id="pacificMapStatus" style="display:none;">
            <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <div class="pacific-map-status__title">Live SST imagery unavailable right now</div>
              <div class="pacific-map-status__text">NOAA NCEI's ocean tiles are not responding. The map is still live, but the temperature overlay is temporarily unavailable. Please try again shortly.</div>
            </div>
          </div>
        </div>

        <!-- Time Slider -->
        <div class="pacific-slider glass-card">
          <div class="pacific-slider__label">
            <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span id="sliderCurrentLabel">Aug 2026</span>
          </div>
          <div class="pacific-slider__track">
            <span class="pacific-slider__tick-label">Jan 2026</span>
            <input type="range" id="sstTimeSlider"
              min="0" max="7" value="7" step="1"
              class="pacific-slider__input">
            <span class="pacific-slider__tick-label">Aug 2026</span>
          </div>
          <button class="pacific-slider__play" id="sliderPlayBtn" title="Animate">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" id="playIcon">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        </div>

        <!-- Legend -->
        <div class="pacific-legend glass-card" id="pacificLegend">
          <div class="pacific-legend__title">SST Anomaly (°C)</div>
          <div class="pacific-legend__bar">
            <span class="pacific-legend__min">−3°C</span>
            <div class="pacific-legend__gradient" id="legendGradient"></div>
            <span class="pacific-legend__max">+3°C</span>
          </div>
          <div class="pacific-legend__labels">
            <span style="color:#3b82f6">Cooler</span>
            <span style="color:#94a3b8">Near Normal</span>
            <span style="color:#ef4444">Warmer</span>
          </div>
        </div>

        <!-- Niño 3.4 info popup -->
        <div class="nino-info-box glass-card" id="nino34InfoBox">
          <div class="nino-info-box__title">📦 Niño 3.4 Region ${infoTrigger('pacific-nino34')}</div>
          <div class="nino-info-box__coords">5°N – 5°S · 170°W – 120°W</div>
          <div class="nino-info-box__desc">Primary ENSO monitoring zone used for RONI calculation</div>
          <div class="nino-info-box__stats" id="nino34InfoBoxStats">Loading current reading…</div>
        </div>
      </div>
    </div>

    ${infoPanel('pacific-nino34', {
    title: 'Niño 3.4 Region',
    meaning: 'Loading the current reading…',
    implication: 'This does not mean that crops are already experiencing stress. It tells us that an important global climate driver is active.',
    takeaway: 'This map tells us what is happening in the Pacific, not yet what is happening to crops.',
  })}
  `;

  injectPacificMapStyles();
  // Init map after DOM insertion
  requestAnimationFrame(() => initPacificLeafletMap(section));
  loadNino34Stats(section);
  return section;
}

async function loadNino34Stats(section) {
  try {
    const status = await fetchCurrentStatus();
    const statsEl = section.querySelector('#nino34InfoBoxStats');
    if (statsEl) {
      statsEl.innerHTML = `
        <div class="nino-info-box__row"><span>Current SST</span><strong>${status.nino34Sst ?? 'N/A'}°C</strong></div>
        <div class="nino-info-box__row"><span>Normal SST</span><strong>${status.nino34Normal ?? 'N/A'}°C</strong></div>
        <div class="nino-info-box__row"><span>SST Anomaly</span><strong>${status.nino34 >= 0 ? '+' : ''}${status.nino34}°C</strong></div>
        <div class="nino-info-box__row"><span>Period</span><strong>${status.nino34Updated ?? status.updated}</strong></div>
      `;
    }

    const anomVal = parseFloat(status.nino34);
    const warmer = !Number.isNaN(anomVal) && anomVal >= 0;
    const ninoPeriod = status.nino34Updated ?? status.updated;
    const meaning = `The Niño 3.4 region is currently ${warmer ? 'warmer' : 'cooler'} than normally expected for ${ninoPeriod} (${status.nino34Sst ?? 'N/A'}°C actual vs ${status.nino34Normal ?? 'N/A'}°C normal). Why does this matter? Persistent unusual ${warmer ? 'warming' : 'cooling'} in this region is an important feature of ${warmer ? 'El Niño' : 'La Niña'} development.`;
    setInfoMeaning('pacific-nino34', meaning);
  } catch (err) {
    console.error('[PacificMap] Niño 3.4 stats fetch failed:', err.message);
  }
}

const WMS_LOAD_TIMEOUT_MS = 9000;
// 1x1 transparent GIF — the same "cancel token" Leaflet's own internal
// _abortLoading uses: reassigning an <img>'s src cancels its in-flight
// network request in every browser.
const EMPTY_IMAGE_URL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

function watchWmsLayerHealth(section, wmsLayer) {
  const statusEl = section.querySelector('#pacificMapStatus');
  if (!statusEl) return;

  let timeoutId = null;
  let sawTileThisCycle = false;

  const showStatus = () => { statusEl.style.display = 'flex'; };
  const hideStatus = () => { statusEl.style.display = 'none'; };

  // When NOAA's WMS service hangs (connects but never responds, never
  // errors), Leaflet's tile <img> elements sit forever waiting on a
  // response that isn't coming. Browsers keep a tab's "loading" indicator
  // spinning for as long as ANY request is outstanding — so on top of the
  // warning banner, actively cancel those requests once we've concluded
  // the service is down, rather than leaving them to hang indefinitely.
  const abortHungTiles = () => {
    const tiles = wmsLayer._tiles || {};
    for (const key in tiles) {
      const tile = tiles[key];
      const el = tile && tile.el;
      if (el && !el.complete) {
        el.onload = null;
        el.onerror = null;
        el.src = EMPTY_IMAGE_URL;
      }
    }
  };

  const armTimeout = () => {
    sawTileThisCycle = false;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (!sawTileThisCycle) {
        showStatus();
        abortHungTiles();
      }
    }, WMS_LOAD_TIMEOUT_MS);
  };

  // 'loading' fires whenever a new batch of tiles is requested — including
  // after setParams() (mode toggle, time slider) and after pan/zoom.
  wmsLayer.on('loading', armTimeout);
  wmsLayer.on('tileload', () => {
    sawTileThisCycle = true;
    hideStatus();
  });
  wmsLayer.on('tileerror', () => {
    // A handful of edge/polar tile errors is normal for WMS services; only
    // treat it as an outage if NOTHING has loaded successfully this cycle
    // by the time the whole batch finishes.
    if (!sawTileThisCycle) {
      wmsLayer.once('load', () => { if (!sawTileThisCycle) showStatus(); });
    }
  });

  armTimeout();
}

function initPacificLeafletMap(section) {
  const el = section.querySelector('#pacificMapContainer');
  if (!el) return;

  const map = L.map(el, {
    center: PACIFIC_REGION_VIEWS[DEFAULT_DASHBOARD_REGION].center,
    zoom: PACIFIC_REGION_VIEWS[DEFAULT_DASHBOARD_REGION].zoom,
    minZoom: 1,
    maxZoom: 5,
    zoomControl: false,
    attributionControl: false,
  });

  // Base dark tiles (ESRI — no API key needed)
  L.tileLayer(BASE_TILES, {
    maxZoom: 6,
    attribution: 'Basemap: Esri, DeLorme, HERE',
  }).addTo(map);

  // NOAA WMS SST Anomaly layer (default mode)
  let currentMode = 'anom';
  let currentTimeIndex = SLIDER_MONTHS.length - 1;

  const wmsLayer = L.tileLayer.wms(OISST_WMS, {
    layers: 'GHRSST_L4_MUR_Sea_Surface_Temperature_Anomalies',
    format: 'image/png',
    transparent: true,
    version: '1.3.0',
    time: SLIDER_MONTHS[currentTimeIndex],
    opacity: 0.88,
  }).addTo(map);

  // NOAA's THREDDS WMS service occasionally hangs or errors — Leaflet has no
  // built-in way to surface that, so a real outage just renders as a silent
  // blank/dark ocean with no explanation. Watch for it explicitly and tell
  // the user this is NOAA's service, not the dashboard.
  watchWmsLayerHealth(section, wmsLayer);

  // Zoom control
  L.control.zoom({ position: 'bottomright' }).addTo(map);

  // Attribution
  L.control.attribution({ position: 'bottomleft', prefix: false })
    .addAttribution('SST: <a href="https://www.ncei.noaa.gov" target="_blank">NOAA NCEI OISST v2.1</a> | Basemap: Esri Dark Gray Canvas')
    .addTo(map);

  // Niño 3.4 bounding box overlay
  const nino34Bounds = L.latLngBounds([[-5, -170], [5, -120]]);
  const nino34Rect = L.rectangle(nino34Bounds, {
    color: '#8dc53d',
    weight: 2,
    fillColor: 'transparent',
    fillOpacity: 0,
    dashArray: '6, 4',
    className: 'nino34-rect',
  }).addTo(map);

  nino34Rect.bindTooltip(
    '<strong>Niño 3.4 Region</strong><br>5°N–5°S · 170°W–120°W<br>Primary ENSO monitoring zone',
    { sticky: true, className: 'leaflet-tooltip--ess' }
  );

  // Add pulsing label marker at center of Niño 3.4 box
  const nino34Label = L.divIcon({
    className: 'nino34-label-icon',
    html: '<div class="nino34-label-inner">NIÑO 3.4</div>',
    iconSize: [80, 24],
    iconAnchor: [40, 12],
  });
  L.marker([0, -145], { icon: nino34Label, interactive: false }).addTo(map);

  let currentRegionOverlay = null;

  function applyRegionView(regionKey) {
    const view = PACIFIC_REGION_VIEWS[regionKey] || PACIFIC_REGION_VIEWS.asia;
    const bounds = L.latLngBounds(view.bounds);
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: view.zoom });

    if (currentRegionOverlay) map.removeLayer(currentRegionOverlay);
    currentRegionOverlay = L.rectangle(L.latLngBounds(view.marker), {
      color: '#facc15',
      weight: 2,
      fillColor: '#facc15',
      fillOpacity: 0.04,
      dashArray: '5, 5',
      interactive: false,
    }).addTo(map);

    const explainEl = section.querySelector('#pacificModeExplain');
    if (explainEl) {
      explainEl.dataset.regionLabel = view.label;
    }
  }

  function applyMode(mode) {
    currentMode = mode;
    section.querySelectorAll('#sstModeToggle .sst-mode-toggle__btn').forEach(button => {
      button.classList.toggle('active', button.dataset.mode === mode);
    });

    const explainEl = section.querySelector('#pacificModeExplain');
    const regionLabel = explainEl?.dataset.regionLabel || PACIFIC_REGION_VIEWS[DEFAULT_DASHBOARD_REGION].label;

    if (mode === 'anom') {
      wmsLayer.setParams({ layers: 'GHRSST_L4_MUR_Sea_Surface_Temperature_Anomalies' });
      section.querySelector('#pacificLegend').querySelector('.pacific-legend__title').textContent = 'SST Anomaly (°C)';
      updateLegendGradient(section, 'anom');
      if (explainEl) explainEl.innerHTML = `This map is framed for ${regionLabel} and shows where nearby ocean waters are warmer or cooler than normally expected.
        <span class="pacific-mode-explain__sub">Actual SST tells us how warm the ocean is. SST anomaly tells us how unusual that temperature is.</span>`;
    } else {
      wmsLayer.setParams({ layers: 'GHRSST_L4_MUR_Sea_Surface_Temperature' });
      section.querySelector('#pacificLegend').querySelector('.pacific-legend__title').textContent = 'Sea Surface Temp (°C)';
      updateLegendGradient(section, 'sst');
      if (explainEl) explainEl.innerHTML = `This map is framed for ${regionLabel} and shows the actual temperature of nearby ocean waters.
        <span class="pacific-mode-explain__sub">Actual SST tells us how warm the ocean is. SST anomaly tells us how unusual that temperature is.</span>`;
    }
  }

  // --- Mode Toggle ---
  section.querySelector('#sstModeToggle').addEventListener('click', (e) => {
    const btn = e.target.closest('.sst-mode-toggle__btn');
    if (!btn) return;
    applyMode(btn.dataset.mode);
  });

  window.addEventListener('ess:region-change', (e) => {
    applyRegionView(e.detail?.region?.key || DEFAULT_DASHBOARD_REGION);
    applyMode(currentMode);
  });

  // --- Time Slider ---
  const slider = section.querySelector('#sstTimeSlider');
  const sliderLabel = section.querySelector('#sliderCurrentLabel');

  slider.addEventListener('input', () => {
    currentTimeIndex = parseInt(slider.value);
    sliderLabel.textContent = SLIDER_LABELS[currentTimeIndex];
    wmsLayer.setParams({ time: SLIDER_MONTHS[currentTimeIndex] });
  });

  // --- Play Animation ---
  let playing = false;
  let playInterval = null;
  const playBtn = section.querySelector('#sliderPlayBtn');
  const playIcon = section.querySelector('#playIcon');

  playBtn.addEventListener('click', () => {
    playing = !playing;
    playIcon.innerHTML = playing
      ? '<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>'
      : '<path d="M8 5v14l11-7z"/>';

    if (playing) {
      playInterval = setInterval(() => {
        currentTimeIndex = (currentTimeIndex + 1) % SLIDER_MONTHS.length;
        slider.value = currentTimeIndex;
        sliderLabel.textContent = SLIDER_LABELS[currentTimeIndex];
        wmsLayer.setParams({ time: SLIDER_MONTHS[currentTimeIndex] });
      }, 1200);
    } else {
      clearInterval(playInterval);
    }
  });

  applyRegionView(DEFAULT_DASHBOARD_REGION);
  applyMode(currentMode);
}

function updateLegendGradient(section, mode) {
  const bar = section.querySelector('#legendGradient');
  const minLabel = section.querySelector('.pacific-legend__min');
  const maxLabel = section.querySelector('.pacific-legend__max');

  if (mode === 'anom') {
    bar.style.background = 'linear-gradient(to right, #1e40af, #3b82f6, #93c5fd, #f8fafc, #fca5a5, #ef4444, #991b1b)';
    minLabel.textContent = '−3°C';
    maxLabel.textContent = '+3°C';
  } else {
    bar.style.background = 'linear-gradient(to right, #1e3a5f, #1d4ed8, #0ea5e9, #34d399, #facc15, #f97316, #9f1239)';
    minLabel.textContent = '0°C';
    maxLabel.textContent = '32°C';
  }
}

function injectPacificMapStyles() {
  if (document.getElementById('pacific-map-styles')) return;
  const style = document.createElement('style');
  style.id = 'pacific-map-styles';
  style.textContent = `
    #pacific-map { background: var(--clr-deep); }

    .pacific-map-wrap {
      position: relative;
    }

    .pacific-controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: var(--space-3);
      padding: var(--space-3) var(--space-4);
      margin-bottom: var(--space-3);
    }

    .pacific-controls__left, .pacific-controls__right {
      display: flex;
      align-items: center;
      gap: var(--space-4);
    }

    .sst-mode-toggle {
      display: inline-grid;
      grid-template-columns: minmax(120px, auto) minmax(150px, auto);
      gap: 4px;
      max-width: 100%;
      padding: 4px;
      background: var(--clr-surface);
      border: 1px solid var(--clr-border);
      border-radius: var(--radius-lg);
    }

    .sst-mode-toggle__btn {
      min-height: 36px;
      padding: var(--space-2) var(--space-4);
      border-radius: var(--radius-md);
      color: var(--clr-text-secondary);
      font-size: var(--text-sm);
      font-weight: var(--weight-medium);
      line-height: 1.2;
      text-align: center;
      white-space: nowrap;
      transition: background var(--dur-normal) var(--ease-out),
                  color var(--dur-normal) var(--ease-out),
                  box-shadow var(--dur-normal) var(--ease-out);
    }

    .sst-mode-toggle__btn:hover {
      background: var(--clr-surface-2);
      color: var(--clr-text-primary);
    }

    .sst-mode-toggle__btn.active {
      background: var(--clr-accent);
      color: var(--clr-void);
      box-shadow: 0 2px 8px rgba(141,197,61,0.3);
      font-weight: var(--weight-semi);
    }

    #pacificMapContainer {
      border-radius: var(--radius-xl);
      border: 1px solid var(--clr-border);
    }

    .pacific-slider {
      display: flex;
      align-items: center;
      gap: var(--space-4);
      padding: var(--space-3) var(--space-5);
      margin-top: var(--space-3);
    }

    .pacific-slider__label {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-family: var(--font-mono);
      font-size: var(--text-sm);
      font-weight: var(--weight-medium);
      color: var(--clr-accent);
      white-space: nowrap;
      min-width: 80px;
    }

    .pacific-slider__track {
      flex: 1;
      display: flex;
      align-items: center;
      gap: var(--space-3);
    }

    .pacific-slider__tick-label {
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      white-space: nowrap;
    }

    .pacific-slider__input {
      flex: 1;
      -webkit-appearance: none;
      height: 4px;
      background: var(--clr-surface-3);
      border-radius: var(--radius-full);
      outline: none;
      cursor: pointer;
    }

    .pacific-slider__input::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--clr-accent);
      box-shadow: 0 0 0 4px rgba(141,197,61,0.2);
      cursor: pointer;
      transition: box-shadow var(--dur-fast);
    }

    .pacific-slider__input::-webkit-slider-thumb:hover {
      box-shadow: 0 0 0 6px rgba(141,197,61,0.3);
    }

    .pacific-slider__play {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--clr-accent);
      color: var(--clr-void);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all var(--dur-fast);
      box-shadow: 0 0 12px rgba(141,197,61,0.3);
    }

    .pacific-slider__play:hover {
      transform: scale(1.1);
      box-shadow: 0 0 20px rgba(141,197,61,0.5);
    }

    .pacific-legend {
      position: absolute;
      bottom: 120px;
      left: 12px;
      padding: var(--space-3) var(--space-4);
      min-width: 200px;
      z-index: 400;
    }

    .pacific-legend__title {
      font-size: var(--text-xs);
      font-weight: var(--weight-medium);
      color: var(--clr-text-secondary);
      margin-bottom: var(--space-2);
      font-family: var(--font-mono);
    }

    .pacific-legend__bar {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      margin-bottom: var(--space-1);
    }

    .pacific-legend__gradient {
      flex: 1;
      height: 10px;
      border-radius: var(--radius-full);
    }

    .pacific-legend__min, .pacific-legend__max {
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      font-family: var(--font-mono);
    }

    .pacific-legend__labels {
      display: flex;
      justify-content: space-between;
      font-size: 10px;
    }

    .nino-info-box {
      position: absolute;
      top: 60px;
      right: 12px;
      z-index: 400;
      padding: var(--space-3) var(--space-4);
      max-width: 230px;
    }

    .pacific-map-status {
      position: absolute;
      top: var(--space-4);
      left: 50%;
      transform: translateX(-50%);
      z-index: 450;
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      max-width: 480px;
      padding: var(--space-3) var(--space-4);
      background: rgba(20,14,4,0.92);
      border: 1px solid rgba(234,179,8,0.35);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
      backdrop-filter: var(--glass-blur-sm);
    }

    .pacific-map-status svg {
      flex-shrink: 0;
      color: #eab308;
      margin-top: 2px;
    }

    .pacific-map-status__title {
      font-size: var(--text-sm);
      font-weight: var(--weight-semi);
      color: #fde68a;
      margin-bottom: 2px;
    }

    .pacific-map-status__text {
      font-size: var(--text-xs);
      color: rgba(253,230,138,0.75);
      line-height: 1.5;
    }

    .pacific-mode-explain {
      font-size: var(--text-sm);
      color: var(--clr-text-secondary);
      line-height: 1.6;
      margin-bottom: var(--space-3);
    }

    .pacific-mode-explain__sub {
      display: block;
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      margin-top: 2px;
    }

    .nino-info-box__title {
      display: flex;
      align-items: center;
      gap: var(--space-1);
      font-weight: var(--weight-semi);
      font-size: var(--text-sm);
      margin-bottom: var(--space-1);
    }

    .nino-info-box__stats {
      margin-top: var(--space-2);
      padding-top: var(--space-2);
      border-top: 1px solid var(--clr-border);
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nino-info-box__row {
      display: flex;
      justify-content: space-between;
      gap: var(--space-2);
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
    }

    .nino-info-box__row strong {
      color: var(--clr-text-primary);
      font-family: var(--font-mono);
    }

    .nino-info-box__coords {
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      color: var(--clr-accent);
      margin-bottom: var(--space-1);
    }

    .nino-info-box__desc {
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      line-height: 1.4;
    }

    .nino34-label-icon .nino34-label-inner {
      background: rgba(141,197,61,0.15);
      border: 1px solid rgba(141,197,61,0.4);
      border-radius: 4px;
      padding: 2px 6px;
      font-family: var(--font-mono);
      font-size: 10px;
      font-weight: 600;
      color: var(--clr-accent);
      white-space: nowrap;
      letter-spacing: 0.08em;
    }

    .leaflet-tooltip--ess {
      background: var(--clr-surface-3);
      border: 1px solid var(--clr-border-glow);
      border-radius: var(--radius-md);
      color: var(--clr-text-primary);
      font-family: var(--font-body);
      font-size: var(--text-sm);
      padding: 8px 12px;
      box-shadow: var(--shadow-lg);
    }

    @media (max-width: 900px) {
      .pacific-controls {
        align-items: stretch;
      }

      .pacific-controls__left,
      .pacific-controls__right {
        width: 100%;
        justify-content: space-between;
      }

      .sst-mode-toggle {
        width: 100%;
        grid-template-columns: 1fr 1fr;
      }

      .sst-mode-toggle__btn {
        min-width: 0;
        padding-inline: var(--space-2);
        white-space: normal;
      }

      .nino-info-box {
        position: relative;
        top: auto;
        right: auto;
        max-width: none;
        margin-top: var(--space-3);
      }

      .pacific-legend {
        bottom: 18px;
        min-width: 180px;
        max-width: calc(100% - 24px);
      }

      .pacific-slider {
        flex-wrap: wrap;
        gap: var(--space-3);
        padding: var(--space-3);
      }

      .pacific-slider__track {
        order: 3;
        width: 100%;
        flex-basis: 100%;
      }
    }

    @media (max-width: 560px) {
      .pacific-mode-explain {
        font-size: var(--text-xs);
      }

      .pacific-slider__tick-label {
        display: none;
      }

      .pacific-legend {
        position: relative;
        left: auto;
        bottom: auto;
        margin-top: var(--space-3);
        width: 100%;
      }

      .pacific-map-status {
        left: var(--space-3);
        right: var(--space-3);
        transform: none;
        max-width: none;
      }
    }
  `;
  document.head.appendChild(style);
}
